import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  Settings,
  LogOut,
  ChevronsLeft,
  ChevronsRight,
  Hammer,
  ClipboardCheck,
  ShieldCheck,
  BookOpen,
  ClipboardList,
  Sparkles,
  ShieldAlert,
  FileDiff,
  Plug,
  Handshake,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import useAuthStore from '../../stores/authStore';

// ================================================================= //
//          Sidebar Navigation Component for AI Estimator            //
// ================================================================= //
// A modern, collapsible sidebar inspired by professional web apps.  //
// Features:                                                         //
// - Collapsible design for better screen real estate management.    //
// - Active route highlighting to indicate the user's location.      //
// - Themed with the app's professional blue and vibrant green.      //
// - Lucide icons for a clean and modern aesthetic.                  //
// - User profile section with logout functionality.                 //
// ================================================================= //

// --- Type Definition for Navigation Items ---
interface NavItemProps {
  href: string;
  label: string;
  icon: React.ElementType;
}

// --- Navigation Items Configuration ---
// An array of objects to easily manage and render navigation links.
interface NavItemConfig extends NavItemProps {
  /** When set, the item only renders if the user has one of these roles. */
  requiresAnyRole?: string[];
}

const navItems: NavItemConfig[] = [
  { href: '/', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/scope/builder', label: 'Scope Builder', icon: Hammer },
  { href: '/completeness', label: 'Supplement', icon: ClipboardCheck },
  { href: '/comparisons', label: 'Comparisons', icon: FileDiff },
  { href: '/chat', label: 'AI Chat', icon: MessageSquare },
  { href: '/assistant', label: 'AI Assistant', icon: Plug },
  {
    href: '/admin/rules',
    label: 'Rule Library',
    icon: BookOpen,
    requiresAnyRole: ['editor', 'expert', 'reviewer', 'admin', 'super_admin'],
  },
  {
    href: '/admin/rule-suggestions',
    label: 'Rule Suggestions',
    icon: Sparkles,
    requiresAnyRole: ['editor', 'expert', 'admin', 'super_admin'],
  },
  {
    href: '/review/rules',
    label: 'Rule Review',
    icon: ClipboardList,
    requiresAnyRole: ['reviewer', 'admin', 'super_admin'],
  },
  {
    href: '/admin/triage',
    label: 'Rule Triage',
    icon: ShieldAlert,
    requiresAnyRole: ['super_admin'],
  },
  {
    href: '/admin',
    label: 'Admin',
    icon: ShieldCheck,
    requiresAnyRole: ['admin', 'super_admin'],
  },
  // PRP-MCP-10 G1: the affiliate program.
  { href: '/admin/affiliates', label: 'Affiliates', icon: Handshake, requiresAnyRole: ['admin', 'super_admin'] },
  // PRP-MCP-12 G3: suggestions users sent from chat.
  { href: '/admin/suggestions', label: 'Suggestions', icon: ClipboardList, requiresAnyRole: ['admin', 'super_admin'] },
  { href: '/profile', label: 'Settings', icon: Settings },
];

export default function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user, logout, roles } = useAuthStore();
  const location = useLocation();

  const visibleNavItems = navItems.filter((item) => {
    if (!item.requiresAnyRole) return true;
    return item.requiresAnyRole.some((r) => roles.includes(r));
  });

  // Longest-match-wins active route. A bare prefix check lights up parent
  // entries (e.g. /admin) whenever a child path (/admin/rules) is active, so
  // we pick the single nav href that is the most specific match for the
  // current pathname and only highlight that one.
  const activeHref = visibleNavItems.reduce<string | null>((best, item) => {
    const matches =
      location.pathname === item.href ||
      (item.href !== '/' && location.pathname.startsWith(item.href + '/'));
    if (!matches) return best;
    if (!best || item.href.length > best.length) return item.href;
    return best;
  }, null);

  const handleLogout = () => {
    // Accidental taps on the icon used to dump users straight out of the app.
    if (!window.confirm('Sign out? You will need to sign in again to access your projects.')) {
      return;
    }
    logout();
    // The user will be redirected to the login page by the router's logic.
  };

  return (
    <aside
      className={cn(
        // sticky top-0: pin the rail (and its footer) to the viewport even when
        // the page body scrolls — otherwise the user/sign-out block sits at the
        // bottom of the full content height, off-screen on long pages.
        'sticky top-0 hidden h-screen flex-col bg-primary text-primary-foreground transition-all duration-300 ease-in-out md:flex',
        isCollapsed ? 'w-20' : 'w-64'
      )}
    >
      {/* --- Logo and Branding --- */}
      <div className="flex h-16 items-center border-b border-primary-foreground/10 px-6">
        <img
          src="/aiea-logo.svg"
          alt="AI Estimator logo"
          className="h-9 w-9 shrink-0 rounded-md bg-card p-1"
        />
        <span
          className={cn(
            'ml-3 text-lg font-bold transition-opacity',
            isCollapsed && 'opacity-0'
          )}
        >
          AI Estimator
        </span>
      </div>

      {/* --- Main Navigation --- */}
      <nav className="flex-1 space-y-2 p-4">
        {visibleNavItems.map((item) => {
          const isActive = item.href === activeHref;
          return (
            <Link
              key={item.label}
              to={item.href}
              title={item.label}
              className={cn(
                'flex items-center rounded-lg p-3 text-sm font-medium text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10',
                isActive && 'bg-secondary text-secondary-foreground shadow-inner',
                isCollapsed && 'justify-center'
              )}
            >
              <item.icon className="h-5 w-5" />
              <span
                className={cn(
                  'ml-4 whitespace-nowrap transition-all',
                  isCollapsed && 'sr-only opacity-0' // sr-only for accessibility
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* --- Footer Section (Collapse Toggle & User Menu) --- */}
      <div className="mt-auto border-t border-primary-foreground/10 p-4">
        {/* Collapse Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="mb-4 flex w-full items-center rounded-lg p-3 text-sm font-medium text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10"
          title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          {isCollapsed ? (
            <ChevronsRight className="mx-auto h-5 w-5" />
          ) : (
            <>
              <ChevronsLeft className="h-5 w-5" />
              <span className="ml-4">Collapse</span>
            </>
          )}
        </button>

        {/* User Profile & Logout */}
        <div className="flex items-center">
          <img
            src={user?.photoURL || `https://ui-avatars.com/api/?name=${user?.displayName || user?.email}&background=10b981&color=fff`}
            alt={user?.displayName || 'User Avatar'}
            className="h-9 w-9 rounded-full"
          />
          <div
            className={cn(
              'ml-3 flex-1 overflow-hidden transition-opacity',
              isCollapsed && 'opacity-0'
            )}
          >
            <p className="truncate text-sm font-semibold">
              {user?.displayName || 'User'}
            </p>
            <p className="truncate text-xs text-primary-foreground/60">
              {user?.email}
            </p>
          </div>
          <button
            onClick={handleLogout}
            title="Logout"
            className={cn(
              'ml-2 rounded-md p-2 text-primary-foreground/80 transition-colors hover:bg-destructive/80',
              isCollapsed && 'mx-auto'
            )}
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
